import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/DashboardShell";
import { StatusPill } from "@/components/brand";
import MapView from "@/components/MapView";
import api from "@/lib/api";

export default function AdminDispatch() {
  const [items, setItems] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [regionFilter, setRegionFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    const [d, dr, rg] = await Promise.all([api.get("/dispatch"), api.get("/drivers"), api.get("/regions")]);
    setItems(d.data);
    setDrivers(dr.data);
    setRegions(rg.data);
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);

  const reassign = async (id, driverId) => {
    if (!driverId) return;
    try { await api.patch(`/deliveries/${id}/assign`, { driver_id: driverId }); toast.success("Reassigned"); load(); }
    catch { toast.error("Failed"); }
  };
  const flag = async (id) => {
    try { await api.patch(`/deliveries/${id}/status`, { status: "exception", note: "Flagged by dispatch" }); toast.success("Flagged"); load(); }
    catch { toast.error("Failed"); }
  };

  const inTransit = (s) => ["assigned", "picked_up", "en_route"].includes(s);
  const visible = items.filter((d) => (!regionFilter || d.region === regionFilter) && (!statusFilter || d.status === statusFilter));
  const liveDrivers = visible.filter((d) => d.driver_location && inTransit(d.status));
  const regionCounts = items.reduce((acc, d) => { if (d.region) acc[d.region] = (acc[d.region] || 0) + 1; return acc; }, {});
  const regionOptions = Array.from(new Set([...regions.map((r) => r.name), ...items.map((d) => d.region).filter(Boolean)])).sort();
  const points = [
    ...visible.map((d) => ({
      id: `drop-${d.id}`,
      lat: d.dropoff.lat, lng: d.dropoff.lng,
      label: `${d.id.slice(0, 8).toUpperCase()} · ${d.dropoff.address}`,
      type: "dropoff",
    })),
    ...liveDrivers.map((d) => ({
      id: `drv-${d.id}`,
      lat: d.driver_location.lat, lng: d.driver_location.lng,
      label: `${d.driver_name || "Driver"} · ${d.id.slice(0, 8).toUpperCase()} · seen ${new Date(d.driver_location.at).toLocaleTimeString()}`,
      type: "driver",
      eta: d.eta_min,
    })),
  ];
  const trails = liveDrivers.map((d) => d.driver_trail || []);
  const filterCls = "border border-gray-300 bg-white text-xs px-2 py-1.5 font-mono uppercase tracking-wide";

  return (
    <div>
      <PageHeader index="04" eyebrow="Live Dispatch" title="Active deliveries" tag={`${visible.length} of ${items.length} in progress`} />

      <div className="mb-8">
        <div className="flex flex-wrap items-center gap-5 mb-3">
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500" data-testid="live-driver-count">
            <span className={`h-2 w-2 rounded-full ${liveDrivers.length ? "bg-orange-500 animate-pulse" : "bg-gray-300"}`} />
            {liveDrivers.length} driver{liveDrivers.length === 1 ? "" : "s"} live
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">
            <span className="h-2.5 w-2.5 rounded-full bg-orange-500" /> Dropoff
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">
            <span className="h-2.5 w-2.5 rounded-full bg-[#0a0a0a] ring-2 ring-orange-500/30" /> Driver
          </div>
          <div className="ml-auto flex items-center gap-2" data-testid="dispatch-filters">
            <select data-testid="dispatch-region-filter" value={regionFilter} onChange={(e) => setRegionFilter(e.target.value)} className={filterCls}>
              <option value="">All regions</option>
              {regionOptions.map((r) => <option key={r} value={r}>{regionCounts[r] ? `${r} (${regionCounts[r]})` : r}</option>)}
            </select>
            <select data-testid="dispatch-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={filterCls}>
              <option value="">All statuses</option>
              {["assigned", "picked_up", "en_route", "exception"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
            </select>
            {(regionFilter || statusFilter) && (
              <button data-testid="dispatch-clear-filters" onClick={() => { setRegionFilter(""); setStatusFilter(""); }} className="font-mono text-[11px] tracking-[0.1em] uppercase text-gray-500 hover:text-[#0a0a0a] px-2">Clear</button>
            )}
          </div>
        </div>
        <MapView points={points} trails={trails} height={420} />
      </div>

      <div className="border border-gray-200 divide-y divide-gray-200" data-testid="dispatch-list">
        {visible.length === 0 && <div className="px-5 py-10 text-center text-sm text-gray-400">{items.length ? "No deliveries match the current filters." : "No active deliveries across the network."}</div>}
        {visible.map((d) => (
          <div key={d.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-4" data-testid={`dispatch-row-${d.id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-3">
                <span className="font-mono text-[11px] text-gray-400">{d.id.slice(0, 8).toUpperCase()}</span>
                <StatusPill status={d.status} />
                {d.region && <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-gray-400">{d.region}</span>}
                {d.eta_min != null && <span className="font-mono text-[10px] tracking-[0.12em] uppercase text-orange-600" data-testid={`eta-${d.id}`}>~{d.eta_min} min</span>}
              </div>
              <div className="mt-1.5 text-sm text-[#0a0a0a] truncate">{d.pickup.address} → {d.dropoff.address}</div>
              <div className="text-xs text-gray-500">{d.merchant_name} · {d.driver_name || "Unassigned"}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <select data-testid={`reassign-${d.id}`} defaultValue="" onChange={(e) => reassign(d.id, e.target.value)} className="border border-gray-300 text-xs px-2 py-1.5 font-mono uppercase tracking-wide">
                <option value="">Reassign…</option>
                {drivers.map((dr) => <option key={dr.id} value={dr.id}>{dr.name}</option>)}
              </select>
              <button onClick={() => flag(d.id)} data-testid={`flag-${d.id}`} className="font-mono text-[11px] tracking-[0.1em] uppercase border border-orange-500 text-orange-600 px-3 py-1.5 hover:bg-orange-50">Flag</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
